'use client'

import React, { useState, useEffect } from 'react'
import { Strategy } from '@/types'
import { X, Save, Loader2, ArrowRight, ArrowLeft, Shield, Target, AlertTriangle } from 'lucide-react'
import { updateStrategyLogic } from '../lib/api'

interface EditStrategyLogicModalProps {
  strategy: Strategy | null
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const EditStrategyLogicModal: React.FC<EditStrategyLogicModalProps> = ({
  strategy,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    entry_rules: '',
    entry_rules_en: '',
    entry_rules_zh: '',
    exit_rules: '',
    exit_rules_en: '',
    exit_rules_zh: '',
    risk_management: '',
    risk_management_en: '',
    risk_management_zh: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (strategy) {
      setFormData({
        entry_rules: strategy.entry_rules || '',
        entry_rules_en: strategy.entry_rules_en || '',
        entry_rules_zh: strategy.entry_rules_zh || '',
        exit_rules: strategy.exit_rules || '',
        exit_rules_en: strategy.exit_rules_en || '',
        exit_rules_zh: strategy.exit_rules_zh || '',
        risk_management: strategy.risk_management || '',
        risk_management_en: strategy.risk_management_en || '',
        risk_management_zh: strategy.risk_management_zh || ''
      })
    }
  }, [strategy])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!strategy) return

    try {
      setLoading(true)
      setError(null)

      await updateStrategyLogic({
        strategy_id: strategy.id,
        ...formData
      })

      onSuccess()
      onClose()
    } catch (err) {
      console.error('Error updating strategy logic:', err)
      setError(err instanceof Error ? err.message : '更新策略逻辑失败')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (!isOpen || !strategy) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-blue-50/95 dark:bg-blue-900/95 backdrop-blur-md rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-blue-200/50 dark:border-blue-700/50">
        <div className="flex items-center justify-between p-6 border-b border-blue-200/50 dark:border-blue-700/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-blue-900 dark:text-white">编辑策略逻辑</h2>
              <p className="text-sm text-blue-700 dark:text-blue-300">完善策略的入场、出场和风险管理规则</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-white transition-colors p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-4 flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400" />
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 入场规则 */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                                 <div className="flex items-center space-x-3 mb-4">
                   <div className="p-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
                     <ArrowRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                   </div>
                   <div>
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white">入场规则</h3>
                     <p className="text-sm text-gray-600 dark:text-gray-400">定义何时进入交易</p>
                   </div>
                 </div>
                
                <div className="space-y-4">
                                     <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-2"></span>
                       入场条件 (中文)
                     </label>
                     <textarea
                       value={formData.entry_rules}
                       onChange={(e) => handleChange('entry_rules', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
                       placeholder="例如：当价格突破20日均线且RSI < 30时入场做多..."
                     />
                   </div>

                                                        <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-2"></span>
                       入场条件 (English)
                     </label>
                     <textarea
                       value={formData.entry_rules_en}
                       onChange={(e) => handleChange('entry_rules_en', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
                       placeholder="e.g., Enter long when price breaks above 20-day MA and RSI < 30..."
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full mr-2"></span>
                       入场条件 (中文详细版)
                     </label>
                     <textarea
                       value={formData.entry_rules_zh}
                       onChange={(e) => handleChange('entry_rules_zh', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 transition-all"
                       placeholder="详细描述入场逻辑、信号确认和时机选择..."
                     />
                   </div>
                </div>
              </div>
            </div>

            {/* 出场规则 */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                                 <div className="flex items-center space-x-3 mb-4">
                   <div className="p-2 bg-green-500/20 rounded-lg border border-green-500/30">
                     <ArrowLeft className="w-4 h-4 text-green-600 dark:text-green-400" />
                   </div>
                   <div>
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white">出场规则</h3>
                     <p className="text-sm text-gray-600 dark:text-gray-400">定义何时退出交易</p>
                   </div>
                 </div>
                
                <div className="space-y-4">
                                                        <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mr-2"></span>
                       出场条件 (中文)
                     </label>
                     <textarea
                       value={formData.exit_rules}
                       onChange={(e) => handleChange('exit_rules', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/20 transition-all"
                       placeholder="例如：当价格跌破10日均线或达到止盈目标时出场..."
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mr-2"></span>
                       出场条件 (English)
                     </label>
                     <textarea
                       value={formData.exit_rules_en}
                       onChange={(e) => handleChange('exit_rules_en', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/20 transition-all"
                       placeholder="e.g., Exit when price breaks below 10-day MA or reaches profit target..."
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mr-2"></span>
                       出场条件 (中文详细版)
                     </label>
                     <textarea
                       value={formData.exit_rules_zh}
                       onChange={(e) => handleChange('exit_rules_zh', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/20 transition-all"
                       placeholder="详细描述出场逻辑、止盈止损策略和风险控制..."
                     />
                   </div>
                </div>
              </div>
            </div>

            {/* 风险管理 */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                                 <div className="flex items-center space-x-3 mb-4">
                   <div className="p-2 bg-red-500/20 rounded-lg border border-red-500/30">
                     <Shield className="w-4 h-4 text-red-600 dark:text-red-400" />
                   </div>
                   <div>
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white">风险管理</h3>
                     <p className="text-sm text-gray-600 dark:text-gray-400">保护资金安全</p>
                   </div>
                 </div>
                
                <div className="space-y-4">
                                     <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-red-500 dark:bg-red-400 rounded-full mr-2"></span>
                       风险控制 (中文)
                     </label>
                     <textarea
                       value={formData.risk_management}
                       onChange={(e) => handleChange('risk_management', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/20 transition-all"
                       placeholder="例如：单笔交易最大风险2%，总仓位不超过10%..."
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-red-500 dark:bg-red-400 rounded-full mr-2"></span>
                       风险控制 (English)
                     </label>
                     <textarea
                       value={formData.risk_management_en}
                       onChange={(e) => handleChange('risk_management_en', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/20 transition-all"
                       placeholder="e.g., Max 2% risk per trade, total position size < 10%..."
                     />
                   </div>

                   <div>
                     <label className="block text-sm font-medium text-blue-900 dark:text-white mb-2 flex items-center">
                       <span className="w-2 h-2 bg-red-500 dark:bg-red-400 rounded-full mr-2"></span>
                       风险控制 (中文详细版)
                     </label>
                     <textarea
                       value={formData.risk_management_zh}
                       onChange={(e) => handleChange('risk_management_zh', e.target.value)}
                       rows={6}
                       className="w-full px-4 py-3 bg-white/80 dark:bg-blue-800/50 border border-blue-200 dark:border-blue-600/50 rounded-lg text-blue-900 dark:text-white focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/20 transition-all"
                       placeholder="详细描述仓位管理、止损策略和风险监控机制..."
                     />
                   </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-blue-200/50 dark:border-blue-700/50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-white transition-colors rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50"
              disabled={loading}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center px-6 py-3 whitespace-nowrap"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              保存更改
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditStrategyLogicModal 